/**
 * Create and initialise Tako dropdown buttons
 */
import { getLogger } from './utils/logger-context';
import { resolveComponentScope } from './utils/resolveComponentScope';

/* -----------------------------------------------------------------------------
 * Tako Dropdown Button
 * -------------------------------------------------------------------------- */

const dropdownInstances = new Map();

/**
 * Initialize Tako Dropdown
 */

const COMPONENT_NAME = 'Dropdown';

export const init = (unresolvedScope) => {
    const logger = getLogger();
    const scope = resolveComponentScope(unresolvedScope);

    const merge = (originalObject, overrides) => {
        return Object.values(
            []
                .concat(originalObject, overrides)
                .reduce(
                    (properties, currentProperty) => (
                        (properties[currentProperty.name] = Object.assign(
                            properties[currentProperty.name] || {},
                            currentProperty,
                        )),
                        properties
                    ),
                    {},
                ),
        );
    };

    const defaultLoggerArgs = {
        componentName: COMPONENT_NAME,
        scope,
    };
    logger.debug(`Initialising ${COMPONENT_NAME} component.`, { scope });

    const allTakoDropdowns = scope.querySelectorAll('.tako-dropdown__wrapper');

    allTakoDropdowns.forEach((takoDropdown) => {
        try {
            const button = takoDropdown.querySelector('.tako-button--dropdown');
            const dropdown = takoDropdown.querySelector('.tako-dropdown');

            if (!button || !dropdown) {
                logger.warn(
                    'Tako dropdown wrappers must contain a button and a dropdown menu. Add these inside the `tako-dropdown__wrapper` using the `tako-button--dropdown` and `tako-dropdown` classes.',
                );
                return;
            }

            button.setAttribute('aria-expanded', false);
            dropdown.setAttribute('aria-hidden', true);

            const isTakoIconButton =
                button.classList.contains('tako-button--icon');

            // eslint-disable-next-line no-undef
            const popperDropdownInstance = Popper.createPopper(
                button,
                dropdown,
                {
                    placement: 'bottom-start',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 2],
                            },
                        },
                    ],
                },
            );

            const setPopperDropdownOptions = (newModifier) => {
                popperDropdownInstance.setOptions((options) =>
                    Object.assign(options, {
                        modifiers: merge(options.modifiers, newModifier),
                    }),
                );
            };

            const dropdownItems = dropdown.querySelectorAll(
                '.tako-dropdown__item:not(.tako-dropdown__item--disabled)',
            );
            dropdownItems.forEach((item) =>
                item.setAttribute('tabindex', '-1'),
            );

            const open = () => {
                const closeIconHTML =
                    '<i class="far fa-angle-up" aria-hidden="true" title="Close dropdown"></i>';

                dropdown.setAttribute('data-open', true);
                button.classList.add('active');
                if (!isTakoIconButton) {
                    const angleDownIcon =
                        button.querySelector('.fa-angle-down');
                    if (angleDownIcon) {
                        angleDownIcon.outerHTML = closeIconHTML;
                    }
                }

                setPopperDropdownOptions([
                    { name: 'eventListeners', enabled: true },
                ]);

                popperDropdownInstance.update();

                button.setAttribute('aria-expanded', true);
                dropdown.setAttribute('aria-hidden', false);

                // Emit a custom event when the dropdown is opened
                const openEvent = new CustomEvent('takoDropdownOpen', {
                    detail: {
                        dropdown,
                        button,
                        id: takoDropdown.id,
                    },
                });
                takoDropdown.dispatchEvent(openEvent);
            };

            const close = () => {
                const openIconHTML =
                    '<i class="far fa-angle-down" aria-hidden="true" title="Open dropdown"></i>';

                dropdown.removeAttribute('data-open');
                button.classList.remove('active');
                if (!isTakoIconButton) {
                    const angleUpIcon = button.querySelector('.fa-angle-up');
                    if (angleUpIcon) {
                        angleUpIcon.outerHTML = openIconHTML;
                    }
                }

                setPopperDropdownOptions([
                    { name: 'eventListeners', enabled: false },
                ]);

                button.setAttribute('aria-expanded', false);
                dropdown.setAttribute('aria-hidden', true);

                // Emit a custom event when the dropdown is closed
                const closeEvent = new CustomEvent('takoDropdownClose', {
                    detail: {
                        dropdown,
                        button,
                        id: takoDropdown.id,
                    },
                });
                takoDropdown.dispatchEvent(closeEvent);
            };

            const handleClick = () => {
                if (dropdown.hasAttribute('data-open')) {
                    close();
                } else {
                    open();
                }
            };

            const handleKeydown = (event) => {
                if (
                    event.target.classList.contains('tako-dropdown__item') &&
                    (event.key === 'Enter' || event.key === ' ')
                ) {
                    event.target.click();
                }

                // We ignore these events, and let them perform their default action
                if (
                    event.key === 'Space' ||
                    event.key === 'Enter' ||
                    event.key === 'Tab'
                ) {
                    return;
                }

                event.preventDefault();

                const items = Array.from(
                    dropdown.querySelectorAll('.tako-dropdown__item'),
                );

                let index = items.indexOf(event.target);

                if (event.key === 'ArrowUp' && index > 0) {
                    index -= 1;
                }

                if (event.key === 'ArrowDown' && index < items.length - 1) {
                    index += 1;
                }

                if (index < 0) {
                    index = 0;
                }

                items[index].focus();
            };

            button.addEventListener('click', handleClick);

            button.addEventListener('keydown', handleKeydown);

            dropdownItems.forEach((item) => {
                item.addEventListener('keydown', handleKeydown);
            });

            dropdownItems.forEach((item) =>
                item.addEventListener('click', close),
            );

            if (
                takoDropdown.getAttribute('data-close-onclick-outside') !==
                'false'
            ) {
                document.addEventListener(
                    'click',
                    function closeOnClickOutside(event) {
                        if (
                            !(
                                event.target === button ||
                                event.target.classList.contains(
                                    'tako-dropdown',
                                ) ||
                                event.target.classList.contains(
                                    'tako-dropdown__item',
                                )
                            ) &&
                            dropdown.hasAttribute('data-open')
                        ) {
                            close();
                        }
                    },
                );
            }
            /**
             * if dropdown button is part of a modal
             * - add an attribute to the dropdown to change its z-index
             */
            if (button.closest('.tako-modal__dialog')) {
                dropdown.setAttribute('data-modal-content', true);
            }

            // Store the instance for external control via `toggle`
            dropdownInstances.set(takoDropdown, { open, close });
        } catch (error) {
            logger.error(error, {
                ...defaultLoggerArgs,
                element: takoDropdown,
            });
        }
    });
};

/**
 * Tako Dropdown Functions
 */

const debounceDelay = 100;
const debounceTimeouts = new Map();

/**
 * Toggles the visibility of one or more Tako Dropdown components.
 *
 * @param {string} selector - Document query selector (id or class) to target the dropdown(s).
 *                            Accepts an id (e.g., '#my-dropdown') or a class (e.g., '.my-dropdown-class').
 * @param {boolean|null} [open=null] - Optional. If `true`, explicitly open the dropdown(s).
 *                                     If `false`, explicitly close the dropdown(s).
 *                                     If `null` (default), the dropdown's current state will be toggled.
 *
 * @example
 * // Toggle based on the current state (open if closed, close if open):
 * toggleDropdown('.my-dropdown-class');
 *
 * @example
 * // Explicitly open the dropdown(s):
 * toggleDropdown('#my-dropdown-id', true);
 *
 * @example
 * // Explicitly close the dropdown(s):
 * toggleDropdown('.my-dropdown-class', false);
 */
export const toggle = (selector, open = null) => {
    const logger = getLogger();

    // Support id's without the # prefix
    if (selector[0] !== '#' && selector[0] !== '.') {
        selector = `#${selector}`;
    }

    // Get all elements that match the selector (could be multiple if class selector is used)
    const takoDropdownElements = document.querySelectorAll(selector);

    if (!takoDropdownElements.length) {
        logger.warn(
            `No Tako Dropdown components found with the selector "${selector}".`,
        );
        return;
    }

    takoDropdownElements.forEach((takoDropdownElement) => {
        const instance = dropdownInstances.get(takoDropdownElement);

        if (!instance) {
            logger.warn(
                `No instance found for the element with the selector "${selector}". Make sure the Tako dropdown has been initialized.`,
            );
            return;
        }

        const dropdown = takoDropdownElement.querySelector('.tako-dropdown');

        // Clear any existing timeout for this dropdown
        if (debounceTimeouts.has(takoDropdownElement)) {
            clearTimeout(debounceTimeouts.get(takoDropdownElement));
        }

        const shouldOpen =
            open !== null ? open : !dropdown.hasAttribute('data-open'); // If `open` is null, toggle based on current state

        // Handle debounce delay to ensure proper timing
        debounceTimeouts.set(
            takoDropdownElement,
            setTimeout(() => {
                if (shouldOpen) {
                    instance.open();
                } else {
                    instance.close();
                }

                // Remove the timeout once it's executed
                debounceTimeouts.delete(takoDropdownElement);
            }, debounceDelay),
        );
    });
};
